/**
 * The typed query-key factory.
 *
 * CLAUDE.md §6: **every** key starts with workspace and brand scope. That is
 * not cosmetic — it is what makes `queryClient.removeQueries({ queryKey:
 * workspaceScope(id) })` a complete tenant eviction when the switcher moves,
 * so one tenant's cached rows can never be rendered under another's header.
 *
 * Keys are `as const` tuples, so a typo is a type error rather than a silent
 * cache miss that shows a spinner forever.
 */

export type WorkspaceScope = readonly ['workspace', string];
export type BrandScope = readonly ['workspace', string, 'brand', string];

/** Everything owned by one workspace. Used for eviction, never for a query. */
export function workspaceScope(workspaceId: string): WorkspaceScope {
  return ['workspace', workspaceId] as const;
}

/**
 * Everything owned by one brand.
 *
 * `brandId` is `null` for workspace-level resources so the tuple shape stays
 * uniform: a workspace-level key is a brand key whose brand is "none", not a
 * differently-shaped key that eviction would miss.
 */
export function brandScope(workspaceId: string, brandId: string | null): BrandScope {
  return ['workspace', workspaceId, 'brand', brandId ?? 'none'] as const;
}

export interface FactFilters {
  readonly state: string;
}

/**
 * The window a metrics read covers.
 *
 * Part of the key rather than a mutable filter held beside it: two windows are
 * two different answers, and caching them under one key would show a reader
 * last month's coverage under this month's heading.
 */
export interface MetricFilters {
  readonly from: string;
  readonly to: string;
}

/** The calendar's window and state filter; part of the key, never beside it. */
export interface ScheduleFilters {
  readonly from: string;
  readonly to: string;
  readonly state: string;
}

export const queryKeys = {
  brands: (workspaceId: string) => [...brandScope(workspaceId, null), 'brands'] as const,

  brand: (workspaceId: string, brandId: string) =>
    [...brandScope(workspaceId, brandId), 'brand'] as const,

  /** Prefix matching every fact-list filter for one brand. Use it to invalidate. */
  factsAll: (workspaceId: string, brandId: string) =>
    [...brandScope(workspaceId, brandId), 'facts'] as const,

  facts: (workspaceId: string, brandId: string, filters: FactFilters) =>
    [...brandScope(workspaceId, brandId), 'facts', filters] as const,

  factEvidence: (workspaceId: string, factId: string) =>
    [...brandScope(workspaceId, null), 'fact-evidence', factId] as const,

  approvals: (workspaceId: string) =>
    [...brandScope(workspaceId, null), 'approval-requests'] as const,

  approval: (workspaceId: string, approvalId: string) =>
    [...brandScope(workspaceId, null), 'approval-requests', approvalId] as const,

  contentVersion: (workspaceId: string, versionId: string) =>
    [...brandScope(workspaceId, null), 'content-version', versionId] as const,

  contentHistory: (workspaceId: string, contentItemId: string) =>
    [...brandScope(workspaceId, null), 'content-history', contentItemId] as const,

  properties: (workspaceId: string, brandId: string) =>
    [...brandScope(workspaceId, brandId), 'properties'] as const,

  /** Prefix matching every metrics window for one brand. Use it to invalidate. */
  metricsAll: (workspaceId: string, brandId: string) =>
    [...brandScope(workspaceId, brandId), 'metrics'] as const,

  metrics: (workspaceId: string, brandId: string, filters: MetricFilters) =>
    [...brandScope(workspaceId, brandId), 'metrics', filters] as const,

  connectors: (workspaceId: string) => [...brandScope(workspaceId, null), 'connectors'] as const,

  publication: (workspaceId: string, publicationId: string) =>
    [...brandScope(workspaceId, null), 'publication', publicationId] as const,

  // --- Campaigns and calendar (web-campaigns) ---------------------------------

  /** Prefix for every campaign list of one brand. Use it to invalidate. */
  campaigns: (workspaceId: string, brandId: string) =>
    [...brandScope(workspaceId, brandId), 'campaigns'] as const,

  /**
   * One campaign, addressed by id. The brand is not known until it loads, so
   * it sits under the workspace's "none" brand like every id-addressed read.
   */
  campaign: (workspaceId: string, campaignId: string) =>
    [...brandScope(workspaceId, null), 'campaign', campaignId] as const,

  campaignBrief: (workspaceId: string, campaignId: string) =>
    [...brandScope(workspaceId, null), 'campaign', campaignId, 'brief'] as const,

  campaignApproaches: (workspaceId: string, campaignId: string) =>
    [...brandScope(workspaceId, null), 'campaign', campaignId, 'approaches'] as const,

  campaignDeliverables: (workspaceId: string, campaignId: string) =>
    [...brandScope(workspaceId, null), 'campaign', campaignId, 'deliverables'] as const,

  campaignTrackingLinks: (workspaceId: string, campaignId: string) =>
    [...brandScope(workspaceId, null), 'campaign', campaignId, 'tracking-links'] as const,

  campaignAudienceSegments: (workspaceId: string, brandId: string) =>
    [...brandScope(workspaceId, brandId), 'audience-segments'] as const,

  campaignConversionGoals: (workspaceId: string, propertyId: string) =>
    [...brandScope(workspaceId, null), 'conversion-goals', propertyId] as const,

  campaignSession: (workspaceId: string) => [...brandScope(workspaceId, null), 'session'] as const,

  /** The workspace member directory (`GET /workspaces/{id}/members`). */
  members: (workspaceId: string) => [...brandScope(workspaceId, null), 'members'] as const,

  /** Prefix for every calendar window. Use it to invalidate. */
  schedulesAll: (workspaceId: string) => [...brandScope(workspaceId, null), 'schedules'] as const,

  schedules: (workspaceId: string, filters: ScheduleFilters) =>
    [...brandScope(workspaceId, null), 'schedules', filters] as const,

  // --- Learning loop: analytics, experiments, recommendations (web-learning) --

  /** Prefix for every overview window of one brand. Use it to invalidate. */
  analyticsOverviewAll: (workspaceId: string, brandId: string) =>
    [...brandScope(workspaceId, brandId), 'analytics-overview'] as const,

  analyticsOverview: (workspaceId: string, brandId: string, filters: MetricFilters) =>
    [...brandScope(workspaceId, brandId), 'analytics-overview', filters] as const,

  campaignAnalytics: (workspaceId: string, campaignId: string, filters: MetricFilters) =>
    [...brandScope(workspaceId, null), 'campaign', campaignId, 'analytics', filters] as const,

  campaignContent: (workspaceId: string, campaignId: string) =>
    [...brandScope(workspaceId, null), 'campaign', campaignId, 'content'] as const,

  experiments: (workspaceId: string, campaignId: string) =>
    [...brandScope(workspaceId, null), 'campaign', campaignId, 'experiments'] as const,

  experiment: (workspaceId: string, experimentId: string) =>
    [...brandScope(workspaceId, null), 'experiment', experimentId] as const,

  agentRun: (workspaceId: string, runId: string) =>
    [...brandScope(workspaceId, null), 'agent-run', runId] as const,

  /** Prefix for every status filter of one brand's queue. Use it to invalidate. */
  recommendationsAll: (workspaceId: string, brandId: string) =>
    [...brandScope(workspaceId, brandId), 'recommendations'] as const,

  recommendations: (workspaceId: string, brandId: string, status: string) =>
    [...brandScope(workspaceId, brandId), 'recommendations', status] as const,

  // --- SEO / ASO (web-seo) ----------------------------------------------------

  seoAudits: (workspaceId: string, brandId: string, propertyId: string) =>
    [...brandScope(workspaceId, brandId), 'seo-audits', propertyId] as const,

  seoAudit: (workspaceId: string, brandId: string, auditId: string) =>
    [...brandScope(workspaceId, brandId), 'seo-audit', auditId] as const,

  seoOpportunities: (workspaceId: string, brandId: string, propertyId: string) =>
    [...brandScope(workspaceId, brandId), 'seo-opportunities', propertyId] as const,

  asoWorkspace: (workspaceId: string, brandId: string, propertyId: string) =>
    [...brandScope(workspaceId, brandId), 'aso', propertyId] as const,

  metricImport: (workspaceId: string, brandId: string, importId: string) =>
    [...brandScope(workspaceId, brandId), 'metric-import', importId] as const,
} as const;

export type QueryKey =
  ReturnType<(typeof queryKeys)[keyof typeof queryKeys]> | WorkspaceScope | BrandScope;

// --- Workspace, portfolio, brand tabs, settings (web-workspace) --------------

/**
 * Pre-tenant keys: the signed-in user and the workspaces they can open.
 *
 * The one documented exception to the workspace-first rule — these answer
 * "which tenant?", so they cannot be scoped by one. They hold identifiers and
 * names only, never tenant data, so leaving them out of a tenant eviction
 * cannot leak one workspace's rows into another's screen.
 */
export const sessionKeys = {
  me: () => ['session', 'me'] as const,
  workspaces: () => ['session', 'workspaces'] as const,
} as const;

export const workspaceKeys = {
  workspace: (workspaceId: string) => [...brandScope(workspaceId, null), 'workspace'] as const,

  portfolio: (workspaceId: string) => [...brandScope(workspaceId, null), 'portfolio'] as const,

  /** Same tuple as `queryKeys.campaignAudienceSegments`, so one invalidation serves both. */
  audienceSegments: (workspaceId: string, brandId: string) =>
    [...brandScope(workspaceId, brandId), 'audience-segments'] as const,

  brandBookVersions: (workspaceId: string, brandId: string) =>
    [...brandScope(workspaceId, brandId), 'brand-book-versions'] as const,

  /** Same tuple as `queryKeys.campaignConversionGoals`, so one invalidation serves both. */
  conversionGoals: (workspaceId: string, propertyId: string) =>
    [...brandScope(workspaceId, null), 'conversion-goals', propertyId] as const,

  auditEventsAll: (workspaceId: string) =>
    [...brandScope(workspaceId, null), 'audit-events'] as const,

  auditEvents: (workspaceId: string, filters: Readonly<Record<string, string>>) =>
    [...brandScope(workspaceId, null), 'audit-events', filters] as const,

  costLedger: (workspaceId: string, filters: Readonly<Record<string, string>>) =>
    [...brandScope(workspaceId, null), 'cost-ledger', filters] as const,

  agentRunsAll: (workspaceId: string) => [...brandScope(workspaceId, null), 'agent-runs'] as const,

  agentRuns: (workspaceId: string, filters: Readonly<Record<string, string>>) =>
    [...brandScope(workspaceId, null), 'agent-runs', filters] as const,

  agentRun: (workspaceId: string, runId: string) =>
    [...brandScope(workspaceId, null), 'agent-run', runId] as const,
} as const;
